/**
 * R3-Phase-3 — viewer player at `/v/:token`.
 *
 * STRICTLY ISOLATED from the operator app: this component must not
 * import `@/components/Layout`, `@/lib/api`, `@/lib/auth`, or any
 * workflow/runs component.  It speaks ONLY to `lib/viewerApi`.
 *
 * Lifecycle:
 *  1. mount → consumeMagicLink(token) -> 200 | 410 | 404 | other
 *  2. on 200, if confidentiality_ack required & not yet acked, show
 *     the blocking ack modal.  On accept, POST /acknowledge then
 *     drop into the player.
 *  3. player renders with custom chrome, watermark overlay, heartbeats,
 *     4 h hard-cap on session length.
 *
 *  Any 401/410/403 during the session immediately tears down the
 *  player and shows the expiry message.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import {
  consumeMagicLink,
  acknowledge as ackConfidentiality,
  heartbeat,
  heartbeatBeacon,
  streamUrl,
  sessionInfo,
  ViewerSessionExpiredError,
  ViewerNotValidError,
} from "@/lib/viewerApi";

const SESSION_HARD_CAP_MS = 4 * 60 * 60 * 1000; // 4 h
const HEARTBEAT_INTERVAL_MS = 10_000;            // every 10 s while playing
const HEARTBEAT_MIN_GAP_MS = 1_000;              // client-side rate-limit

// ---------------------------------------------------------------------------
// Tiny inline icons (no lucide-react import to keep the bundle isolated).
// ---------------------------------------------------------------------------
const Icon = (path) =>
  function I({ size = 16, className = "" }) {
    return (
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
      >
        {path}
      </svg>
    );
  };
const PlayIcon = Icon(<polygon points="6 4 20 12 6 20 6 4" fill="currentColor" stroke="none" />);
const PauseIcon = Icon(
  <>
    <rect x="6" y="4" width="4" height="16" fill="currentColor" stroke="none" />
    <rect x="14" y="4" width="4" height="16" fill="currentColor" stroke="none" />
  </>,
);
const VolumeIcon = Icon(
  <>
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  </>,
);
const MuteIcon = Icon(
  <>
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </>,
);
const FsIcon = Icon(
  <>
    <polyline points="4 9 4 4 9 4" />
    <polyline points="20 9 20 4 15 4" />
    <polyline points="4 15 4 20 9 20" />
    <polyline points="20 15 20 20 15 20" />
  </>,
);
const FsExitIcon = Icon(
  <>
    <polyline points="9 4 4 4 4 9" />
    <polyline points="15 4 20 4 20 9" />
    <polyline points="9 20 4 20 4 15" />
    <polyline points="15 20 20 20 20 15" />
  </>,
);
const SpinnerIcon = Icon(
  <>
    <circle cx="12" cy="12" r="10" opacity="0.2" />
    <path d="M22 12a10 10 0 0 1-10 10" />
  </>,
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatTime(t) {
  if (!Number.isFinite(t)) return "0:00";
  const s = Math.max(0, Math.floor(t));
  const mm = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, "0");
  if (mm < 60) return `${mm}:${ss}`;
  const hh = Math.floor(mm / 60);
  return `${hh}:${String(mm % 60).padStart(2, "0")}:${ss}`;
}

function formatExpiry(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Chrome
// ---------------------------------------------------------------------------
function BrandRow() {
  return (
    <div className="flex items-center gap-2.5" data-testid="viewer-brand-row">
      <img
        src="/eyvo-logo.jpg"
        alt="Eyvo"
        width={28}
        height={28}
        className="h-7 w-7 rounded-md"
      />
      <span className="text-sm font-semibold tracking-tight text-slate-900">
        Eyvo Demo Agent
      </span>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div
      className="min-h-screen w-full bg-slate-50 text-slate-900"
      data-testid="viewer-page-root"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <BrandRow />
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-10">{children}</main>
      <footer className="mt-16 border-t border-slate-200 bg-white py-4">
        <div
          className="mx-auto flex max-w-5xl items-center justify-center gap-2 text-xs text-slate-400"
          data-testid="viewer-footer"
        >
          <img
            src="/eyvo-logo.jpg"
            alt=""
            width={14}
            height={14}
            className="h-3.5 w-3.5 rounded-sm opacity-70"
          />
          Powered by Eyvo Demo Agent
        </div>
      </footer>
    </div>
  );
}

function LoadingState() {
  return (
    <Shell>
      <div
        className="flex items-center gap-2 text-sm text-slate-500"
        data-testid="viewer-loading"
      >
        <SpinnerIcon size={14} className="animate-spin" />
        Preparing your demo…
      </div>
    </Shell>
  );
}

function InvalidState() {
  return (
    <Shell>
      <h1
        className="text-3xl font-semibold tracking-tight text-slate-900"
        data-testid="viewer-invalid-title"
      >
        This invitation is no longer valid
      </h1>
      <p
        className="mt-4 max-w-prose text-sm leading-6 text-slate-600"
        data-testid="viewer-invalid-body"
      >
        The link has expired, was revoked, or was never issued. Please
        contact the person who shared the demo with you if you believe
        this is a mistake.
      </p>
    </Shell>
  );
}

function ExpiredMidSession() {
  return (
    <Shell>
      <h1
        className="text-3xl font-semibold tracking-tight text-slate-900"
        data-testid="viewer-expired-title"
      >
        Your viewing session has expired
      </h1>
      <p
        className="mt-4 max-w-prose text-sm leading-6 text-slate-600"
        data-testid="viewer-expired-body"
      >
        Please request a new invitation from the demo owner.
      </p>
    </Shell>
  );
}

function GenericError({ onRetry }) {
  return (
    <Shell>
      <h1
        className="text-2xl font-semibold tracking-tight text-slate-900"
        data-testid="viewer-error-title"
      >
        Something went wrong
      </h1>
      <p className="mt-4 text-sm leading-6 text-slate-600">
        We couldn't load your demo right now. Please try again in a moment.
      </p>
      <button
        type="button"
        onClick={onRetry}
        data-testid="viewer-error-retry"
        className="mt-8 inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500"
      >
        Try again
      </button>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Confidentiality acknowledgment modal
// ---------------------------------------------------------------------------
function ConfidentialityModal({ onAgree, busy }) {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ack-title"
      data-testid="viewer-ack-modal"
    >
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <h2
          id="ack-title"
          className="text-lg font-semibold tracking-tight text-slate-900"
        >
          Confidentiality acknowledgment
        </h2>
        <p className="mt-3 text-sm leading-6 text-slate-700">
          This video contains confidential Eyvo intellectual property.
          By clicking <strong>I agree</strong> you confirm you will not
          redistribute, record, or share access. Your viewing session
          is logged.
        </p>
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onAgree}
            disabled={busy}
            data-testid="viewer-ack-agree"
            className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? "…" : "I agree"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Watermark overlay — repeating diagonal text
// ---------------------------------------------------------------------------
function Watermark({ text }) {
  // 4 x 3 grid of rotated text spans.  pointer-events:none so video
  // controls aren't blocked.  Color: white with slight text-shadow.
  const cells = [];
  for (let r = 0; r < 4; r += 1) {
    for (let c = 0; c < 3; c += 1) {
      cells.push(
        <span
          key={`${r}-${c}`}
          className="whitespace-nowrap text-[12px] font-medium tracking-wide text-white"
          style={{
            opacity: 0.18,
            textShadow: "0 1px 2px rgba(0,0,0,0.6)",
          }}
        >
          {text}
        </span>,
      );
    }
  }
  return (
    <div
      className="pointer-events-none absolute inset-0 grid select-none"
      style={{
        gridTemplateColumns: "repeat(3, 1fr)",
        gridTemplateRows: "repeat(4, 1fr)",
        transform: "rotate(-25deg) scale(1.5)",
        placeItems: "center",
      }}
      aria-hidden="true"
      data-testid="viewer-watermark"
    >
      {cells}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main player
// ---------------------------------------------------------------------------
function Player({ invitation, onSessionLost }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffering, setBuffering] = useState(true);
  const [errored, setErrored] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tick, setTick] = useState(0); // forces watermark text refresh every 30s

  const sessionStartRef = useRef(Date.now());
  const lastHbRef = useRef(0);
  const lastHbTypeRef = useRef(null);

  const everPlayedRef = useRef(false);

  // Heartbeat helper (throttled).
  const fireHb = useCallback(
    async (event_type, force = false) => {
      const now = Date.now();
      if (!force && now - lastHbRef.current < HEARTBEAT_MIN_GAP_MS) return;
      lastHbRef.current = now;
      lastHbTypeRef.current = event_type;
      try {
        await heartbeat(event_type, videoRef.current?.currentTime ?? 0);
      } catch (err) {
        if (
          err instanceof ViewerSessionExpiredError ||
          err instanceof ViewerNotValidError
        ) {
          onSessionLost();
        }
      }
    },
    [onSessionLost],
  );

  // Periodic heartbeat while playing.
  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      fireHb("video_heartbeat");
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [playing, fireHb]);

  // 4 h hard cap.
  useEffect(() => {
    const id = setTimeout(() => {
      try {
        videoRef.current?.pause();
      } catch {
        /* noop */
      }
      onSessionLost();
    }, SESSION_HARD_CAP_MS);
    return () => clearTimeout(id);
  }, [onSessionLost]);

  // Force a fresh range request on every mount.  In production the
  // browser performs the fetch directly from the <video src> prop, so
  // this useEffect is a no-op idempotent reload.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.load();
    } catch {
      /* noop */
    }
  }, [invitation.invitation_id]);

  // Watermark timestamp refresh every 30 s.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // Beacon on unload — record video_completed if still playing.
  useEffect(() => {
    function handle() {
      if (lastHbTypeRef.current === "video_completed") return;
      heartbeatBeacon(
        "video_completed",
        videoRef.current?.currentTime ?? 0,
      );
    }
    window.addEventListener("pagehide", handle);
    return () => window.removeEventListener("pagehide", handle);
  }, []);

  // Fullscreen detection.
  useEffect(() => {
    function onFs() {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    }
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // Keyboard shortcuts.
  useEffect(() => {
    function onKey(e) {
      const v = videoRef.current;
      if (!v) return;
      if (
        e.target &&
        (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
      ) {
        return;
      }
      if (e.code === "Space") {
        e.preventDefault();
        if (v.paused) v.play();
        else v.pause();
      } else if (e.code === "ArrowRight") {
        e.preventDefault();
        v.currentTime = Math.min((v.currentTime || 0) + 5, v.duration || 0);
      } else if (e.code === "ArrowLeft") {
        e.preventDefault();
        v.currentTime = Math.max((v.currentTime || 0) - 5, 0);
      } else if (e.key === "f" || e.key === "F") {
        toggleFullscreen();
      } else if (e.key === "m" || e.key === "M") {
        setMuted((m) => !m);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  function toggleFullscreen() {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      el.requestFullscreen?.();
    }
  }

  function togglePlay() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  }

  function onTimeUpdate() {
    setCurrentTime(videoRef.current?.currentTime ?? 0);
  }
  function onDurationChange() {
    setDuration(videoRef.current?.duration ?? 0);
  }
  function onPlay() {
    everPlayedRef.current = true;
    setPlaying(true);
    setBuffering(false);
    if (lastHbTypeRef.current === "video_paused") fireHb("video_resumed", true);
    else if (lastHbTypeRef.current !== "video_started") fireHb("video_started", true);
  }
  function onPause() {
    setPlaying(false);
    if (lastHbTypeRef.current !== "video_completed") fireHb("video_paused", true);
  }
  function onEnded() {
    setPlaying(false);
    fireHb("video_completed", true);
  }
  function onWaiting() {
    setBuffering(true);
  }
  function onPlaying() {
    setBuffering(false);
  }
  function onError(e) {
    // React strict mode (dev) double-mounts the component, aborting the
    // first video element's metadata request which fires `error` on the
    // abandoned element.  MediaError.code === 1 is MEDIA_ERR_ABORTED;
    // we ignore it.  We also ignore ANY error that fires before the
    // user pressed play at least once — by that point the metadata
    // negotiation has succeeded so any failure is real.
    const mediaErr = e?.target?.error;
    if (!mediaErr || mediaErr.code === 1) return;
    if (!everPlayedRef.current) return;
    setErrored(true);
  }
  function onLoadedData() {
    setErrored(false);
  }

  function onSeek(e) {
    const v = videoRef.current;
    if (!v) return;
    const next = Number(e.target.value);
    if (Number.isFinite(next)) v.currentTime = next;
  }

  function onVolumeChange(e) {
    const next = Number(e.target.value);
    setVolume(next);
    if (videoRef.current) videoRef.current.volume = next;
    if (next > 0 && muted) setMuted(false);
  }

  // Compose watermark text: viewer email • company • timestamp
  const ts = new Date(sessionStartRef.current + tick * 30_000)
    .toISOString()
    .replace("T", " ")
    .slice(0, 19);
  const wmText = [
    invitation.viewer_email,
    invitation.company_name,
    `${ts} UTC`,
  ]
    .filter(Boolean)
    .join(" • ");

  const expiresStr = formatExpiry(invitation.expires_at);

  return (
    <Shell>
      <div className="mb-6">
        <h1
          className="text-3xl font-semibold tracking-tight text-slate-900"
          data-testid="viewer-page-title"
        >
          {invitation.video_title || "Eyvo Demo"}
        </h1>
        <div
          className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500"
          data-testid="viewer-page-meta"
        >
          <span data-testid="viewer-page-email">{invitation.viewer_email}</span>
          {invitation.company_name ? (
            <>
              <span>·</span>
              <span data-testid="viewer-page-company">
                {invitation.company_name}
              </span>
            </>
          ) : null}
          {expiresStr ? (
            <>
              <span>·</span>
              <span data-testid="viewer-page-expires">
                Access expires {expiresStr}
              </span>
            </>
          ) : null}
        </div>
      </div>

      {errored ? (
        <div
          className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800"
          data-testid="viewer-playback-error"
        >
          Playback error. Please refresh the page.
        </div>
      ) : (
        <div
          ref={containerRef}
          className="relative overflow-hidden rounded-xl bg-slate-950 shadow-lg"
          style={{ aspectRatio: "16 / 9" }}
          data-testid="viewer-player-container"
        >
          {/* eslint-disable-next-line */}
          <video
            ref={videoRef}
            src={streamUrl(invitation.invitation_id)}
            className="absolute inset-0 h-full w-full bg-black"
            preload="metadata"
            playsInline
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            onContextMenu={(e) => e.preventDefault()}
            onTimeUpdate={onTimeUpdate}
            onDurationChange={onDurationChange}
            onPlay={onPlay}
            onPause={onPause}
            onEnded={onEnded}
            onWaiting={onWaiting}
            onPlaying={onPlaying}
            onLoadedData={onLoadedData}
            onError={onError}
            muted={muted}
            data-testid="viewer-video-element"
          />

          <Watermark text={wmText} />

          {buffering && !errored ? (
            <div
              className="pointer-events-none absolute inset-0 grid place-items-center"
              data-testid="viewer-buffering"
            >
              <span className="rounded-full bg-black/60 p-3 text-white">
                <SpinnerIcon size={28} className="animate-spin" />
              </span>
            </div>
          ) : null}

          {!playing && !buffering ? (
            <button
              type="button"
              onClick={togglePlay}
              className="absolute inset-0 grid place-items-center bg-black/0 transition hover:bg-black/10 focus:outline-none"
              aria-label="Play"
              data-testid="viewer-center-play"
            >
              <span className="grid h-20 w-20 place-items-center rounded-full bg-white/85 text-slate-900 shadow-xl backdrop-blur transition hover:scale-105 hover:bg-white">
                <PlayIcon size={32} />
              </span>
            </button>
          ) : null}

          {/* Bottom control bar */}
          <div
            className="absolute inset-x-0 bottom-0 flex flex-col gap-1 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-4 pb-3 pt-8"
            data-testid="viewer-controls"
          >
            <input
              type="range"
              min={0}
              max={Number.isFinite(duration) && duration > 0 ? duration : 0}
              step={0.1}
              value={currentTime}
              onChange={onSeek}
              className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/30 accent-indigo-400"
              aria-label="Seek"
              data-testid="viewer-seek"
            />
            <div className="flex items-center gap-3 text-white">
              <button
                type="button"
                onClick={togglePlay}
                className="grid h-9 w-9 place-items-center rounded-full text-white hover:bg-white/10"
                aria-label={playing ? "Pause" : "Play"}
                data-testid="viewer-play-toggle"
              >
                {playing ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
              </button>
              <div
                className="font-mono text-xs tabular-nums text-white/80"
                data-testid="viewer-time"
              >
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
              <button
                type="button"
                onClick={() => setMuted((m) => !m)}
                className="ml-auto grid h-9 w-9 place-items-center rounded-full text-white hover:bg-white/10"
                aria-label={muted ? "Unmute" : "Mute"}
                data-testid="viewer-mute-toggle"
              >
                {muted || volume === 0 ? <MuteIcon size={18} /> : <VolumeIcon size={18} />}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                onChange={onVolumeChange}
                className="h-1 w-20 cursor-pointer appearance-none rounded-full bg-white/30 accent-indigo-400"
                aria-label="Volume"
                data-testid="viewer-volume"
              />
              <button
                type="button"
                onClick={toggleFullscreen}
                className="grid h-9 w-9 place-items-center rounded-full text-white hover:bg-white/10"
                aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                data-testid="viewer-fs-toggle"
              >
                {isFullscreen ? <FsExitIcon size={18} /> : <FsIcon size={18} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Top-level state machine
// ---------------------------------------------------------------------------
export default function ViewerPlaceholderPage() {
  const { token } = useParams();
  const [state, setState] = useState({ kind: "loading" });
  const [ackBusy, setAckBusy] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const invitation = await consumeMagicLink(token);
      const needsAck =
        invitation.require_confidentiality_ack &&
        !invitation.confidentiality_already_acked;
      setState({ kind: needsAck ? "ack" : "play", invitation });
    } catch (err) {
      if (err instanceof ViewerNotValidError) {
        setState({ kind: "invalid" });
      } else {
        setState({ kind: "error" });
      }
    }
  }, [token]);

  const handleAck = useCallback(async () => {
    setAckBusy(true);
    try {
      await ackConfidentiality();
      // Refresh the projection so confidentiality_already_acked flips.
      const fresh = await sessionInfo();
      setState({ kind: "play", invitation: fresh });
    } catch {
      setState({ kind: "expired" });
    } finally {
      setAckBusy(false);
    }
  }, []);

  const handleLost = useCallback(() => {
    setState({ kind: "expired" });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (state.kind === "loading") return <LoadingState />;
  if (state.kind === "invalid") return <InvalidState />;
  if (state.kind === "expired") return <ExpiredMidSession />;
  if (state.kind === "error") return <GenericError onRetry={load} />;
  // state.kind is "ack" or "play" — render Player in BOTH branches with the
  // same JSX shape so React keeps the same video element mounted across the
  // ack→play transition (otherwise the in-flight Range request gets aborted
  // and the video element fires `error`, dropping us into the errored state).
  return (
    <>
      <Player invitation={state.invitation} onSessionLost={handleLost} />
      {state.kind === "ack" ? (
        <ConfidentialityModal onAgree={handleAck} busy={ackBusy} />
      ) : null}
    </>
  );
}
